'use client';

import { motion } from 'motion/react';
import { SectionLayout } from '@/components/shared/section-layout';
import SectionHeader from '@/components/shared/section-header';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { Carousel } from '@/components/sections/recent-works/carousel';
import ProjectCard from '@/components/sections/recent-works/card';
import {
  getProjectDisplayImage,
  getProjectPhotoImages,
  type ProjectImage,
  type SanityProject,
} from '@/sanity/project-data';
import type { RecentWorksSectionContent } from '@/sanity/lib/sections';

function projectToCard(project: SanityProject) {
  const image: ProjectImage | null = getProjectDisplayImage(project);
  if (!image) return null;

  const projectImages = getProjectPhotoImages(project);

  return {
    src: image.src,
    alt: image.alt,
    title: project.title,
    location: project.location ?? undefined,
    type: project.type,
    description: project.description ?? undefined,
    delivered: project.delivered ?? undefined,
    duration: project.duration ?? undefined,
    status: project.status ?? undefined,
    mediaSections: [
      {
        id: 'photos',
        label: 'Images',
        images: projectImages,
      },
      {
        id: 'renderings',
        label: 'Renderings',
        images: project.renders,
      },
    ],
    beforeAfter: project.beforeAfter.map((comparison) => ({
      title: comparison.title,
      before: {
        src: comparison.beforeSrc,
        alt: comparison.beforeAlt,
        title: comparison.title,
      },
      after: {
        src: comparison.afterSrc,
        alt: comparison.afterAlt,
        title: comparison.title,
      },
    })),
  };
}

export default function RecentWorks({
  projects,
  content,
}: {
  projects: SanityProject[];
  content?: RecentWorksSectionContent | null;
}) {
  const { sectionSequenceRevealVariants, sectionSequenceBlockVariants } =
    useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();
  const { header } = content ?? {};
  const cards = projects
    .map(projectToCard)
    .filter((card): card is Exclude<ReturnType<typeof projectToCard>, null> => card !== null);

  if (!content || content.show === false) return null;

  const showCarouselBlock = content.showCarousel !== false && cards.length > 0;
  const { primary, secondary } = sectionCtaPairFromContent(content.showCTA, content.ctaButtons);
  const hasHeaderBody = Boolean(header?.tagline || header?.heading?.length || header?.subheading);

  if (!showCarouselBlock && !hasHeaderBody && !primary) return null;

  return (
    <SectionLayout
      id="our-recent-works"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-neutral-900 dark:bg-black dark:border-t py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        className="space-y-6 sm:space-y-12"
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
      >
        {hasHeaderBody ? (
          <SectionHeader
            sequenceChild
            content={{
              eyebrow: header?.tagline,
              description: header?.subheading,
            }}
            titleSlot={
              <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-white">
                <SanityInlineText
                  blocks={header?.heading}
                  emphasisClassName="italic font-normal text-[#FCFAD7] dark:text-[#FCFAD7]"
                />
              </h2>
            }
            eyebrowClassName="text-[#FCFAD7]/80 dark:text-[#FCFAD7]/80"
            emphasisClassName="text-[#FCFAD7] dark:text-[#FCFAD7]"
            headingClassName="text-white"
            subheadingClassName="text-muted-foreground"
          />
        ) : null}
        {primary ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <SectionCtaPair
              primary={primary}
              secondary={secondary}
              trackingLocation="recent_works"
            />
          </motion.div>
        ) : null}
        {showCarouselBlock ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <Carousel
              showControls={content.showCarouselControls !== false}
              enableAutoplay={content.enableAutoplay !== false}
              items={cards.slice(0, 6).map((card, i) => (
                <ProjectCard key={card.src} card={card} index={i} layout />
              ))}
            />
          </motion.div>
        ) : null}
      </motion.div>
    </SectionLayout>
  );
}
